import * as React from "react";
import { User } from "lucide-react";

import { cn } from "@/lib/utils";

function Card({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="card"
      className={cn("bg-card text-card-foreground flex flex-col rounded-xl shadow-sm", className)}
      {...props}
      data-oid="e:mgr6h"
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 mt-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
      data-oid="qjdidc1"
    />
  );
}

function CardHeaderWithIcon({
  icon,
  iconColor = "blue",
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & { 
  icon?: React.ReactNode;
  iconColor?: "blue" | "purple" | "teal" | "green" | "orange" | "red" | "pink" | "indigo";
}): React.ReactElement {
  const colorClasses = {
    blue: "bg-blue-500",
    purple: "bg-purple-500",
    teal: "bg-teal-500",
    green: "bg-green-500",
    orange: "bg-orange-500",
    red: "bg-red-500",
    pink: "bg-pink-500",
    indigo: "bg-indigo-500",
  };

  return (
    <div
      data-slot="card-header"
      className={cn("flex items-center gap-3 px-6 py-4", className)}
      {...props}
    >
      <div className={cn("rounded-lg p-2.5 flex items-center justify-center", colorClasses[iconColor])}>
        {icon ?? <User size={20} className="text-white" />}
      </div>
      <div className="text-base font-semibold">
        {children}
      </div>
    </div>
  );
}

function CardIconHeader({
  icon,
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & { icon?: React.ReactNode }): React.ReactElement {
  return (
    <div
      data-slot="card-header"
      className={cn("flex items-center gap-3 px-3 py-0 pb-2 pt-2 border-b border-gray-200", className)}
      {...props}
      data-oid="ueqb-kw"
    >
      <div className="rounded-full bg-teal-100/50 p-2.5 flex items-center justify-center" data-oid="k3uyo23">
        {icon ?? <User size={22} className="text-teal-700" data-oid="i0vkx9a" />}
      </div>
      <div className="text-m font-semibold" data-oid="q7cr8t.">
        {children}
      </div>
    </div>
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div data-slot="card-title" className={cn("leading-none font-semibold", className)} {...props} data-oid="l4b-nqn" />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
      data-oid="uj:gjlh"
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="card-action"
      className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)}
      {...props}
      data-oid="5136q95"
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
  return <div data-slot="card-content" className={cn("px-6 py-6", className)} {...props} data-oid="9.lro.x" />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 mb-6 [.border-t]:pt-6", className)}
      {...props}
      data-oid="l-k-xim"
    />
  );
}

export { Card, CardHeader, CardHeaderWithIcon, CardFooter, CardTitle, CardAction, CardDescription, CardContent, CardIconHeader };
